import React, { FC, useEffect, useRef, useState } from 'react';
import classes from './EmployersReportsPage.module.scss';
import EmployersReportTable from '../../../components/Admin/Tables/AnalyticsAndReports/EmployersReportTable/EmployersReportsTable';
import HeaderAnalyticsTables from '../../../components/Admin/HeaderAnalyticsAndReports/HeaderAnalyticsTables';
import { useGetEmployeesReport } from '../../../hooks/Admin/AnalyticsAndReportsTables/EmployeesReport/useGetEmployeesReport';
import { currentDate } from '../../../utils/Admin/AnalyticsAndReports/formatAnalyticAndReportsDate';
import { EmployeesHeader } from '../../../types/enums';
import { saveAsCSV } from '../../../utils/Admin/AnalyticsAndReports/saveAsCSV';
import BranchQueueFilter from '../../../components/Admin/BranchQueueFilter/BranchQueueFilter';
import { useAdminQueueIdsFilter } from '../../../hooks/Admin/useAdminQueueIdsFilter';
import { useGetEmployeesForReportFilter } from '../../../hooks/Admin/AnalyticsAndReportsTables/EmployeesReport/useGetEmployeesForReportFilter';
import ReactSelect, { type StylesConfig } from 'react-select';

const tabsList = ['Период'];
const isActiveButtonCSV = true;

type EmployeeOption = { label: string; value: number | null };

const EmployersReportPage: FC = () => {
  const [activeTab, setActiveTab] = useState(tabsList[0]);
  const [selectedStartDate, setSelectedStartDate] = useState(currentDate);
  const [selectedEndDate, setSelectedEndDate] = useState(currentDate);
  const [filterBranchIds, setFilterBranchIds] = useState<number[]>([]);
  const [filterQueueIds, setFilterQueueIds] = useState<number[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const selectedBranchQueueId = filterQueueIds.length ? filterQueueIds[0] : null;
  const isQueueSelected = selectedBranchQueueId != null;
  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const prevQueueIdRef = useRef<number | null>(null);
  const menuPortalTarget = typeof window !== 'undefined' ? document.body : undefined;
  const { employees: employeesForFilter, isLoading: isLoadingEmployeesForFilter } =
    useGetEmployeesForReportFilter();

  useEffect(() => {
    setSelectedEmployeeId(null);
  }, [selectedBranchQueueId]);

  useEffect(() => {
    const prev = prevQueueIdRef.current;
    if (prev !== null && selectedBranchQueueId !== null && prev !== selectedBranchQueueId) {
      setSelectedStartDate(currentDate);
      setSelectedEndDate(currentDate);
      setSelectedEmployeeId(null);
    }
    prevQueueIdRef.current = selectedBranchQueueId;
  }, [selectedBranchQueueId]);

  const employeesInSelectedQueue = selectedBranchQueueId
    ? employeesForFilter.filter((e) => e.queueIds.includes(selectedBranchQueueId))
    : [];

  const employeeOptions: EmployeeOption[] = React.useMemo(() => {
    const opts = employeesInSelectedQueue.map((e) => ({ label: e.label, value: e.id }));
    return [{ label: 'Все', value: null }, ...opts];
  }, [employeesInSelectedQueue]);

  const selectedEmployeeOption: EmployeeOption | null = React.useMemo(() => {
    if (selectedEmployeeId == null) return employeeOptions[0] ?? null;
    return employeeOptions.find((o) => o.value === selectedEmployeeId) ?? null;
  }, [employeeOptions, selectedEmployeeId]);

  const selectStyles: StylesConfig<EmployeeOption, false> = React.useMemo(
    () => ({
      container: (base) => ({ ...base, width: 250, maxWidth: 250 }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        height: 38,
        borderRadius: 8,
        borderColor: state.isFocused ? '#5aa2dc' : 'var(--gray-border)',
        boxShadow: 'none',
        backgroundColor: 'var(--white)',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-roboto-400)',
        fontSize: 15,
        ':hover': {
          borderColor: state.isDisabled ? 'var(--gray-border)' : '#5aa2dc'
        }
      }),
      input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 15, lineHeight: '20px' }),
      valueContainer: (base) => ({ ...base, padding: '0 10px' }),
      singleValue: (base) => ({
        ...base,
        color: '#404040',
        fontSize: 15,
        lineHeight: '20px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%'
      }),
      placeholder: (base) => ({ ...base, color: '#404040', fontSize: 15, lineHeight: '20px' }),
      indicatorSeparator: (base) => ({ ...base, display: 'none' }),
      dropdownIndicator: (base) => ({ ...base, padding: 6 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, width: 250, maxWidth: 250, fontSize: 15 }),
      menuList: (base) => ({ ...base, maxHeight: 240, fontSize: 15 }),
      option: (base, state) => ({
        ...base,
        fontSize: 15,
        lineHeight: '20px',
        cursor: 'pointer',
        backgroundColor: state.isFocused ? 'rgba(0, 0, 0, 0.04)' : 'var(--white)',
        color: '#404040'
      })
    }),
    []
  );

  const selectedDate = {
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    branchQueueId: selectedBranchQueueId,
    employeeId: selectedEmployeeId
  };
  const {
    employeesReportResp: employeesReportData,
    refetchEmployeesReport,
    isLoadingEmployeesReport
  } = useGetEmployeesReport(selectedDate);

  const handleSaveAsCSV = () => saveAsCSV(employeesReportData, EmployeesHeader);

  useEffect(() => {
    if (isQueueSelected) {
      refetchEmployeesReport();
    }
  }, [selectedStartDate, selectedEndDate, selectedBranchQueueId, selectedEmployeeId]);

  return (
    <div className={classes.container}>
      <HeaderAnalyticsTables
        tabs={tabsList}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        handleSaveAsCSV={handleSaveAsCSV}
        isActiveButtonCSV={isActiveButtonCSV && isQueueSelected}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        setSelectedStartDate={setSelectedStartDate}
        setSelectedEndDate={setSelectedEndDate}
        name="Отчет по сотрудникам"
        filterContent={
          <div className={classes.filtersRow}>
            <BranchQueueFilter
              selectedBranchIds={filterBranchIds}
              selectedQueueIds={filterQueueIds}
              onBranchChange={setFilterBranchIds}
              onQueueChange={setFilterQueueIds}
              singleQueue
              queueIdsFilter={queueIdsFilter}
            />
            <div className={classes.employeeSelect}>
              <div className={classes.employeeLabel}>Сотрудник</div>
              <ReactSelect<EmployeeOption, false>
                className={classes.employeeControl}
                placeholder="Все"
                noOptionsMessage={() => 'Нет сотрудников'}
                isDisabled={!isQueueSelected || isLoadingEmployeesForFilter}
                isClearable={false}
                value={selectedEmployeeOption}
                options={employeeOptions}
                onChange={(opt) => setSelectedEmployeeId(opt?.value ?? null)}
                styles={selectStyles}
                menuPlacement="bottom"
                menuPosition="fixed"
                menuPortalTarget={menuPortalTarget}
                maxMenuHeight={240}
              />
            </div>
          </div>
        }
      />

      {!isQueueSelected ? (
        <div className={classes.emptyState}>
          Выберите очередь в фильтре, чтобы отобразить данные
        </div>
      ) : (
        <EmployersReportTable
          employeesReportData={employeesReportData}
          isLoadingData={isLoadingEmployeesReport}
        />
      )}
    </div>
  );
};

export default EmployersReportPage;
